import React, { useState, useEffect } from 'react'
import { Link, useSearchParams, useNavigate } from 'react-router-dom'
import { api } from '../api/client'
import { useAuth } from '../context/useAuth'
import PageShell from '../components/ui/PageShell'
import Card from '../components/ui/Card'
import SectionTitle from '../components/ui/SectionTitle'
import { PrimaryButton, SecondaryButton } from '../components/ui/Button'

export default function BuyCredits() {
  const { refreshUser } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const success = searchParams.get('success') === '1'
  const [data, setData] = useState({ enabled: false, products: [], message: '' })
  const [loading, setLoading] = useState(true)
  const [checkoutProductId, setCheckoutProductId] = useState(null)
  const [checkoutError, setCheckoutError] = useState('')

  useEffect(() => {
    api.get('/billing/products')
      .then((r) => setData(r.data || { enabled: false, products: [], message: '' }))
      .catch(() => setData({ enabled: false, products: [], message: 'Credits können beim Admin angefragt werden.' }))
      .finally(() => setLoading(false))
  }, [])

  const startCheckout = async (productId) => {
    setCheckoutError('')
    setCheckoutProductId(productId)
    try {
      const { data: res } = await api.post('/billing/create-checkout-session', {
        product_id: productId,
        success_url: `${window.location.origin}/dashboard/buy-credits?success=1`,
        cancel_url: `${window.location.origin}/dashboard/buy-credits`,
      })
      if (res?.url) {
        window.location.href = res.url
        return
      }
      setCheckoutError(res?.message || 'Checkout nicht verfügbar.')
    } catch (err) {
      setCheckoutError(err?.message || 'Anfrage fehlgeschlagen.')
    } finally {
      setCheckoutProductId(null)
    }
  }

  useEffect(() => {
    if (success) refreshUser()
  }, [success, refreshUser])

  return (
    <PageShell>
      <div className="mx-auto max-w-2xl space-y-6">
        <div className="flex items-center gap-4">
          <Link to="/dashboard" className="text-white/70 hover:text-white text-sm">← Dashboard</Link>
        </div>
        <h1 className="text-2xl font-bold text-white">
          <span className="text-[#00d4aa]">Credits bestellen</span>
        </h1>

        {success && (
          <Card className="p-4 border-green-500/30 bg-green-500/10">
            <p className="text-green-200 font-medium">Vielen Dank. Nach erfolgreicher Zahlung wurden Ihre Credits gutgeschrieben.</p>
            <p className="mt-1 text-sm text-white/70">Falls sie noch nicht sichtbar sind, die Seite kurz aktualisieren.</p>
          </Card>
        )}

        {loading && <p className="text-white/60">Laden…</p>}
        {!loading && !data.enabled && (
          <Card className="p-6">
            <SectionTitle className="mb-2">Credits anfragen</SectionTitle>
            <p className="text-white/80 mb-4">
              Die Zahlungsschnittstelle ist derzeit nicht aktiv. Bitte wenden Sie sich an den Administrator, um Credits zu erhalten.
            </p>
            <SecondaryButton onClick={() => navigate('/dashboard')}>Zurück zum Dashboard</SecondaryButton>
          </Card>
        )}
        {!loading && data.enabled && data.products.length === 0 && (
          <Card className="p-6">
            <p className="text-white/80">Es sind noch keine Credit-Pakete hinterlegt. Bitte beim Admin anfragen.</p>
            <SecondaryButton onClick={() => navigate('/dashboard')} className="mt-4">Zurück zum Dashboard</SecondaryButton>
          </Card>
        )}
        {!loading && data.enabled && data.products.length > 0 && (
          <>
            <p className="text-white/70">Wählen Sie ein Paket. Nach der Zahlung werden die Credits automatisch gutgeschrieben.</p>
            {checkoutError && (
              <div className="rounded-xl border border-amber-500/40 bg-amber-500/15 px-4 py-3 text-sm text-amber-200">
                {checkoutError}
              </div>
            )}
            <div className="grid gap-4 sm:grid-cols-2">
              {data.products.map((p) => (
                <Card key={p.id} className="p-5 flex flex-col">
                  <div className="font-semibold text-white text-lg">{p.name}</div>
                  <div className="text-white/60 mt-1">{p.credits} Credits</div>
                  <div className="mt-4 text-2xl font-bold text-white">
                    {(p.price_cents / 100).toFixed(2)} {p.currency?.toUpperCase()}
                  </div>
                  <div className="mt-4 flex-1" />
                  <PrimaryButton
                    className="w-full mt-4"
                    onClick={() => startCheckout(p.id)}
                    disabled={checkoutProductId !== null}
                  >
                    {checkoutProductId === p.id ? 'Weiterleitung…' : 'Jetzt kaufen'}
                  </PrimaryButton>
                </Card>
              ))}
            </div>
          </>
        )}
      </div>
    </PageShell>
  )
}
